import React from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { usePortalAuth } from "./portalAuth";

const ADMIN_CONSENT_PATTERNS = [
  "AADSTS65001", "admin approval", "needs admin approval", "AADSTS90094", "AADSTS50105",
];

export default function PortalMicrosoftCallback() {
  const navigate = useNavigate();
  const { checkAuth } = usePortalAuth();
  const [error, setError] = React.useState(null);
  const [adminConsentUrl, setAdminConsentUrl] = React.useState(null);
  const [tenantFromError, setTenantFromError] = React.useState("common");
  const processed = React.useRef(false);

  React.useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const err = params.get("error_description") || params.get("error");
    if (err) {
      handleError(err);
      return;
    }
    if (!code || !state) {
      setError("Missing code or state");
      return;
    }
    (async () => {
      try {
        const r = await api.post("/auth/microsoft/callback", { code, state });
        await checkAuth();
        window.history.replaceState({}, "", r.data.redirect_to || "/portal/dashboard");
        navigate(r.data.redirect_to || "/portal/dashboard", { replace: true });
      } catch (e) {
        const detail = e?.response?.data?.detail || "Microsoft sign-in failed";
        handleError(detail);
      }
    })();
  }, [navigate, checkAuth]);

  const handleError = async (msg) => {
    setError(msg);
    const needsAdmin = ADMIN_CONSENT_PATTERNS.some((p) => msg.toLowerCase().includes(p.toLowerCase()));
    if (!needsAdmin) return;
    // Try to pull tenant id from the error
    const tidMatch = msg.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i);
    const tenant = tidMatch ? tidMatch[0] : "common";
    setTenantFromError(tenant);
    try {
      const r = await api.get(`/auth/microsoft/admin-consent-url?tenant=${tenant}&flow=portal&origin=${encodeURIComponent(window.location.origin)}`);
      setAdminConsentUrl(r.data.admin_consent_url);
    } catch (e) {
      // Admin consent URL is a fallback — if we can't build one, the existing error message still shows.
      console.warn("admin-consent URL fetch failed:", e?.message);
    }
  };

  const needsAdmin = adminConsentUrl !== null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="max-w-lg w-full text-center space-y-4">
        {error ? (
          <div className="bg-white border border-slate-200 rounded-lg p-8 text-left shadow-sm">
            <div className="font-heading text-xl font-bold text-slate-900">
              {needsAdmin ? "Your IT admin needs to approve Xentra" : "Portal sign-in failed"}
            </div>
            {needsAdmin ? (
              <>
                <p className="text-sm text-slate-700 mt-4 leading-relaxed">
                  Your organisation's Microsoft 365 settings require an administrator to approve the Xentra CRM customer portal before you can sign in. This is a <strong>one-time approval per company</strong>.
                </p>
                <p className="text-sm text-slate-700 mt-3 leading-relaxed">
                  Please forward the link below to your IT admin (or click it yourself if you have admin rights):
                </p>
                <a
                  href={adminConsentUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-4 block bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-5 py-3 rounded-md text-center"
                  data-testid="portal-admin-consent-link"
                >
                  Approve Xentra CRM for {tenantFromError === "common" ? "your organisation" : "your tenant"}
                </a>
                <div className="text-[11px] text-slate-400 font-mono mt-3 break-all bg-slate-50 p-2 rounded">
                  {adminConsentUrl}
                </div>
                <p className="text-xs text-slate-500 mt-4">
                  After admin approval, return here and click "Continue with Microsoft" again.
                </p>
              </>
            ) : (
              <>
                <div className="text-sm text-slate-600 mt-3">{error}</div>
              </>
            )}
            <a href="/portal/login" className="inline-block mt-6 text-sm text-blue-600 hover:underline">
              ← Back to portal sign-in
            </a>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-3 text-slate-500">
            <div className="h-2 w-2 rounded-full bg-blue-600 pulse-dot" />
            <span className="font-mono text-sm">Completing Microsoft sign-in…</span>
          </div>
        )}
      </div>
    </div>
  );
}
